/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getProjectStatistics } from 'apis/dashboard.api';
import Loading from 'components/Loading';
import { formatShowAppliedFilters, statusColors } from 'utils/func/dashboard';
import DashboardFilter from '../DashboardFilter';

export default function ProjectChartRound({ programs, organizations }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [filters, setFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  // Check if any filters are applied
  const hasActiveFilters =
    appliedFilters.yearProposed?.length > 0 ||
    appliedFilters.organizationId?.length > 0 ||
    appliedFilters.programId?.length > 0;

  const { data: statisticsData, isLoading } = useQuery(
    ['projectStatistics', appliedFilters],
    () => getProjectStatistics(appliedFilters),
    {
      refetchOnWindowFocus: false,
      enabled: isLoadedFilterFromLocalStorage,
    }
  );

  // Process statistics data to create chart data
  useEffect(() => {
    if (statisticsData?.data?.data && Array.isArray(statisticsData.data.data)) {
      const statusMapping = {
        ongoingProject: t('dashboard.projectSubmit.label.ongoingProject'),
        midtermQualifiedProject: t('dashboard.projectSubmit.label.midtermQualifiedProject'),
        midtermNotQualifiedProject: t('dashboard.projectSubmit.label.midtermNotQualifiedProject'),
        acceptanceQualifiedProject: t('dashboard.projectSubmit.label.acceptanceQualifiedProject'),
        acceptanceNotQualifiedProject: t(
          'dashboard.projectSubmit.label.acceptanceNotQualifiedProject'
        ),
        overdueProject: t('dashboard.projectSubmit.label.overdueProject'),
      };

      const labels = [];
      const dataValues = [];
      const colors = Object.values(statusColors.submit);

      const itemsData = [];
      statisticsData?.data?.data.forEach((item) => {
        if (statusMapping[item.status]) {
          labels.push(statusMapping[item.status]);
          dataValues.push(item.count);
          itemsData.push(item);
        }
      });

      const data = {
        labels,
        datasets: [
          {
            data: dataValues,
            backgroundColor: colors.slice(0, dataValues.length),
          },
        ],
      };
      const options = {
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
              generateLabels(chart) {
                const { data: _data } = chart;
                return _data.labels.map((label, i) => {
                  const dataset = _data.datasets[0];
                  const value = dataset.data[i];
                  return {
                    text: `${label} (${value})`,
                    fillStyle: dataset.backgroundColor[i],
                    strokeStyle: dataset.borderColor?.[i] || dataset.backgroundColor[i],
                    lineWidth: 1,
                    index: i,
                    pointStyle: 'circle',
                    hidden: !chart.getDataVisibility(i),
                  };
                });
              },
              font: (ctx) => {
                const { width } = ctx.chart;
                return {
                  size: Math.max(8, width / 30),
                };
              },
            },
            position: 'bottom',
            onClick: () => {},
          },
          datalabels: {
            formatter: (value, ctx) => {
              const item = itemsData[ctx.dataIndex];
              if (item.percentage <= 0) {
                return null;
              }
              return `${item.percentage.toFixed(1)}%`;
            },
            color: '#fff',
            font: {
              weight: 'bold',
              size: 12,
            },
            anchor: 'end',
            align: 'start',
            offset: 20,
          },
          tooltip: {
            callbacks: {
              label(context) {
                const item = itemsData[context.dataIndex];
                return `${t('dashboard.amountOfProjects')}: ${
                  item.count
                } (${item.percentage.toFixed(1)}%)`;
              },
            },
          },
          responsive: true,
        },
        onHover: (event, activeElements) => {
          const canvas = event.native.target;
          canvas.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const clickedIndex = elements[0].index;
            const clickedItem = itemsData[clickedIndex];

            const projectSubmitFilters = {
              yearProposed: appliedFilters.yearProposed,
              organizationId: appliedFilters.organizationId,
              programId: appliedFilters.programId,
              projectStatus: [clickedItem.status],
            };

            localStorage.setItem(
              'admin-projects-submit-filters',
              JSON.stringify(projectSubmitFilters)
            );
            navigate('/projects-submit');
          }
        },
        interaction: {
          intersect: false,
        },
      };

      setChartData(data);
      setChartOptions(options);
    }
  }, [statisticsData, t]);

  useEffect(() => {
    setIsLoadedFilterFromLocalStorage(true);
    const savedFilters = localStorage.getItem('admin-dashboard-projects-submit-filters');
    if (!savedFilters) return;

    setFilters(JSON.parse(savedFilters));
    setAppliedFilters(JSON.parse(savedFilters));
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="card p-3 md:p-5">
      {/* Header Section */}
      <div className="card-header border-bottom-1 surface-border pb-3 mb-4">
        <div className="flex justify-content-between align-items-center">
          <h3 className="text-2xl text-900 font-semibold m-0">
            {t('dashboard.projectSubmit.title')}
          </h3>

          {/* Filter Button - moved to header */}
          <Button
            icon="pi pi-filter"
            label={t('dashboard.filter.label.button')}
            severity={hasActiveFilters ? 'success' : 'secondary'}
            outlined={!hasActiveFilters}
            onClick={() => setShowFilterDialog(true)}
            className="p-button-sm"
          />
        </div>
      </div>

      <DashboardFilter
        showFilterDialog={showFilterDialog}
        onHide={() => setShowFilterDialog(false)}
        filters={filters}
        setFilters={setFilters}
        setAppliedFilters={setAppliedFilters}
        organizations={organizations}
        programs={programs}
        localStorageKey="admin-dashboard-projects-submit-filters"
        isSelectOne={{
          yearProposed: false,
          organizationId: false,
          programId: false,
        }}
      />

      {/* Chart Section */}
      <div className="flex flex-column align-items-center md:px-4 md:pb-4 gap-2">
        {formatShowAppliedFilters(appliedFilters, organizations, programs, t)}

        <Chart
          type="pie"
          data={chartData}
          options={chartOptions}
          plugins={[ChartDataLabels]}
          className="w-full"
          style={{ maxWidth: '400px', height: 'auto' }}
        />
      </div>
    </div>
  );
}

ProjectChartRound.propTypes = {
  programs: PropTypes.array.isRequired,
  organizations: PropTypes.array.isRequired,
};
